import { Injectable, OnDestroy, inject } from '@angular/core';
import { ITcResponse } from '../../../common/models/ITcResponse';

import { HttpService } from '@tc/http';
import { LoggerService, Severity } from '@tc/logger';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, delay, first, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { DropCatchWebApi } from '../../../../environments/endpoints';
import { ApiGetFilters } from '../../../common/contracts/search/ApiGetFilters';
import { ApiSaveSearch } from '../../../common/contracts/search/ApiSaveSearch';
import { ApiGetSavedSearches } from '../../../common/contracts/search/ApiGetSavedSearches';
import { removeSubscriptions } from '../../../common/utilities/remove-subscriptions';
import { FilterDisplayHintType } from './enums/filter-display-hint-type';
import { FilterGroupType } from './enums/filter-group-type';
import { FilterItemType } from './enums/filter-item-type';
import { FilterType } from './enums/filter-type';
import { FilterValueRange } from './models/filter-value-range.model';
import { FilterValue } from './models/filter-value.model';
import { Filter } from './models/filter.model';
import { FilterCollection } from './view-models/filter-collection';
import { FilterGroup } from './view-models/filter-group';
import { FilterItem } from './view-models/filter-item';
import { IFilterAggregation } from './models/filter-aggregation.interface';
import { ISavedSearch, ISavedSearchFilter, SavedSearch } from '../models/ISavedSearch';
import { AuthenticationService } from '@tc/security-oauth-legacy';
import { TableService } from '../../table/table.service';
import { DropCatchItem } from '../../../common/models/DropCatchItem';
import { SortDirectionType } from '../../../common/types/SortDirectionType';

interface ISaveSearchData {
  name: string;
  searchTerm: string;
  filters: Filter[];
}

@Injectable({ providedIn: 'root' })
export class FilterService implements OnDestroy {
  private readonly _loggingService = inject(LoggerService);
  private readonly _authService = inject(AuthenticationService);
  private readonly _http = inject(HttpService);
  private readonly _tableService = inject<TableService<DropCatchItem>>(TableService);

  private filtersChangedSubscription: Subscription;
  private activeSearchSubscription: Subscription;

  private readonly name = 'FilterService';

  private readonly filterCollection = new FilterCollection();
  readonly filterGroups$ = this.filterCollection.Groups$;

  private requestedFiltersSubject = new ReplaySubject<Filter[]>(1);
  readonly requestedFilters$ = this.requestedFiltersSubject.asObservable();

  private filterCountSubject = new ReplaySubject<number>(1);
  readonly filterCount$ = this.filterCountSubject.asObservable();

  private searchTermSubject = new ReplaySubject<string>(1);
  readonly searchTerm$ = this.searchTermSubject.asObservable();

  private savedSearchesSubject = new BehaviorSubject<ISavedSearch[]>([]);
  readonly savedSearches$ = this.savedSearchesSubject.asObservable();

  private activeSearchNameSubject = new ReplaySubject<string>(1);
  readonly activeSearchName$ = this.activeSearchNameSubject.asObservable();

  private activeSearchSubject = new ReplaySubject<ISavedSearch>(1);
  readonly activeSearch$ = this.activeSearchSubject.asObservable();

  private isNewSearchSubject = new ReplaySubject<boolean>(1);
  readonly isNewSearch$ = this.isNewSearchSubject.asObservable();

  readonly tableSorts$ = this._tableService.tableLoaded$.pipe(
    map((table) => table.sorts),
    tap(() => this.checkIsNewSearch()),
    shareReplay(1)
  );

  private readonly _destroyed$ = new Subject<void>();

  constructor() {
    this.isNewSearchSubject.next(false);
    this.createFilterGroups();
    this.filtersChangedSubscription = this.filterCollection.filtersChanged$.subscribe((items) => {
      this.updateFilters(...items);
    });
    this.setDefaultFilters();
    this.initSavedSearchFormDriver();
  }

  ngOnDestroy(): void {
    removeSubscriptions([this.filtersChangedSubscription, this.activeSearchSubscription]);
  }

  setSearchTerm(searchTerm: string): void {
    this.searchTermSubject.next(searchTerm);
    this.updateFilters();
  }

  resetFilters(): void {
    this.activeSearchNameSubject.next('');
    this.searchTermSubject.next('');
    this.filterCollection.reset();
  }

  activateSavedSearch(savedSearchName: string): void {
    this.activeSearchNameSubject.next(savedSearchName);
  }

  // note: doesn't currently support sorts yet (fix this with table service?)
  saveSearch(searchData: ISaveSearchData): Observable<boolean> {
    return this.tableSorts$.pipe(
      first(),
      switchMap((sorts) => {
        const savedSearch: ISavedSearch = new SavedSearch({
          name: searchData.name,
          searchQuery: {
            searchTerm: searchData.searchTerm,
            filters: searchData.filters
              .filter((f) => f.Name !== FilterGroupType.ExpirationDate)
              .map(
                (filter) =>
                  ({
                    name: filter.Name as FilterGroupType,
                    values: filter.Values.map((v) => ({
                      name: v.Value,
                      value: v.Value,
                      range: v.Range ? { Min: v.Range?.Min + '', Max: v.Range?.Max + '' } : v.Range,
                    })),
                  } as ISavedSearchFilter)
              ),
            sorts: sorts.map((s) => ({
              direction: s.direction === 'asc' ? 'Ascending' : 'Descending',
              field: s.field,
            })),
          },
        });
        return this._http.send<ApiSaveSearch, ITcResponse<ISavedSearch>>(new ApiSaveSearch(savedSearch)).pipe(
          first(),
          tap(({ result, success }) => {
            if (success) {
              this.savedSearchesSubject.next([...this.savedSearchesSubject.value.filter((ss) => ss.name !== result.name), result]);
              this.activateSavedSearch(result.name);
            }
          }),
          map((response) => response.success)
        );
      })
    );
  }

  loadSavedSearches(): void {
    const request = new ApiGetSavedSearches();

    // savedSearches only currently works for logged-in users (should we use local-storage for others?)
    this._authService.isAuthenticated$
      .pipe(
        first((isAuthed) => !!isAuthed),
        switchMap(() =>
          this._http.send<ApiGetSavedSearches, ITcResponse<ISavedSearch[]>>(request).pipe(
            first(),
            map((response) => response.result),
            tap((result) => this.savedSearchesSubject.next(result)),
            catchError((error) => of([]))
          )
        )
      )
      .subscribe();
  }

  // TODO: review - when we have new search criteria, reset the dropdown value?
  public checkIsNewSearch(): void {
    combineLatest([this.searchTerm$, this.requestedFilters$, this.activeSearch$.pipe(startWith(null)), this.tableSorts$])
      .pipe(first(), delay(1))
      .subscribe(([searchTerm, filters, activeSearch, sorts]) => {
        const allFilters = this.filterCollection.Groups.map((g) => g.Items).reduce((a, b) => a.concat(b));

        if (searchTerm !== (activeSearch?.searchQuery.searchTerm ?? '')) {
          this.isNewSearchSubject.next(true);
          return;
        }

        if (!activeSearch && filters.length > 1) {
          this.isNewSearchSubject.next(true);
          return;
        }

        // note: we only allow one table sort, and subsquently we should only ever have one active search sort
        const tableSort = sorts.map(({ field, direction }) => ({ field, direction: direction === 'desc' ? 'Descending' : 'Ascending' }))[0];
        const activeSearchSort = activeSearch?.searchQuery.sorts.map(({ direction, field }) => ({ direction, field }))[0];
        function areObjectsEqual(obj1: any, obj2: any): boolean {
          const keys1 = Object.keys(obj1 ?? {});
          const keys2 = Object.keys(obj2 ?? {});
          if (keys1.length !== keys2.length) return false;
          for (const key of keys1) {
            if (obj1[key] !== obj2[key]) {
              return false;
            }
          }
          return true;
        }
        const areSortsEqual = areObjectsEqual(tableSort, activeSearchSort);
        if (!areSortsEqual) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const tldSavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.TLD);
        const tldsSelected: string[] = filters
          .find((f) => f.Name === FilterGroupType.TLD)
          ?.Values.map((v) => v.Value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const tldsActiveSearch: string[] = tldSavedSearchFilter?.values
          .map((v) => v.value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const isDifferentTlds =
          tldsSelected?.length !== tldsActiveSearch?.length ||
          tldsSelected?.some((tld) => !tldsActiveSearch.includes(tld)) ||
          tldsActiveSearch?.some((tld) => !tldsSelected.includes(tld));
        if (isDifferentTlds) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const typesSavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.RecordType);
        const typesSelected: string[] = filters
          .find((f) => f.Name === FilterGroupType.RecordType)
          ?.Values.map((v) => v.Value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const typesActiveSearch: string[] = typesSavedSearchFilter?.values
          .map((v) => v.value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const isDifferentTypes =
          typesSelected?.length !== typesActiveSearch?.length ||
          typesSelected?.some((type) => !typesActiveSearch.includes(type)) ||
          typesActiveSearch?.some((type) => !typesSelected.includes(type));
        if (isDifferentTypes) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const endingTodaySavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.EndingToday);
        const endingTodaySelected: boolean = !!filters.some((f) => f.Name === FilterGroupType.EndingToday);
        const endingTodayActiveSearch: boolean = !!endingTodaySavedSearchFilter;
        const isDifferentEndingToday = endingTodaySelected !== endingTodayActiveSearch;
        if (isDifferentEndingToday) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const filterDomainsSavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.FilterDomains);
        const filterDomainsSelected: string[] = filters
          .find((f) => f.Name === FilterGroupType.FilterDomains)
          ?.Values.map((v) => v.Value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const filterDomainsActiveSearch: string[] = filterDomainsSavedSearchFilter?.values
          .map((v) => v.value)
          .filter((v) => !!allFilters.some((f) => f.IsItemVisible && f.Name === v));
        const isDifferentFilters =
          filterDomainsSelected?.length !== filterDomainsActiveSearch?.length ||
          filterDomainsSelected?.some((filter) => !filterDomainsActiveSearch.includes(filter)) ||
          filterDomainsActiveSearch?.some((filter) => !filterDomainsSelected.includes(filter));
        if (isDifferentFilters) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const lengthSavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.CharacterLength);
        const { Min: minLengthSelected, Max: maxLengthSelected } =
          filters.find((f) => f.Name === FilterGroupType.CharacterLength)?.Values[0]?.Range ?? {};
        const { min: minLengthSaved, max: maxLengthSaved } = lengthSavedSearchFilter?.values[0].range ?? {};
        const isDifferentLength = !(
          (minLengthSelected || 0) === parseInt(minLengthSaved || '0', 10) && (maxLengthSelected || 0) === parseInt(maxLengthSaved || '0', 10)
        );
        if (isDifferentLength) {
          this.isNewSearchSubject.next(true);
          return;
        }

        const priceSavedSearchFilter = activeSearch?.searchQuery.filters.find((f) => f.name === FilterGroupType.PriceRange);
        const { Min: minPriceSelected = null, Max: maxPriceSelected = null } =
          filters.find((f) => f.Name === FilterGroupType.PriceRange)?.Values[0].Range ?? {};
        const { min: minSaved = null, max: maxSaved = null } = priceSavedSearchFilter?.values[0].range ?? {};
        const isDifferent = !((minPriceSelected || 0) === parseInt(minSaved || '0', 10) && (maxPriceSelected || 0) === parseInt(maxSaved || '0', 10));
        if (isDifferent) {
          this.isNewSearchSubject.next(true);
          return;
        }

        // we found no differences to the active search...
        if (!!activeSearch || (filters.length === 1 && !searchTerm && !sorts.length)) {
          this.isNewSearchSubject.next(false);
        }
      });
  }

  private initSavedSearchFormDriver() {
    this.activeSearchSubscription = combineLatest([this.activeSearchName$, this.savedSearches$]).subscribe(([searchName, savedSearches]) => {
      const activeSearch = savedSearches.find((ss) => ss.name === searchName);
      const newSort = activeSearch?.searchQuery.sorts[0];
      this.activeSearchSubject.next(activeSearch);
      this.searchTermSubject.next(activeSearch?.searchQuery.searchTerm || '');

      const filterItems = this.filterCollection.Groups.map((g) => g.Items).reduce((a, b) => a.concat(b));
      const filterItemsChanged = [];

      // check all filter states against active saved search
      for (const filterItemType of Object.values(FilterItemType)) {
        const hasFilter = !!activeSearch?.searchQuery.filters.some((f) => f.values.some((values) => values.name === filterItemType));
        const checkbox = filterItems.find((item) => item.Name === filterItemType);
        if (checkbox && checkbox.IsSelected !== hasFilter) {
          checkbox.Group.setIsActive(true);
          checkbox.setIsSelected(!checkbox.IsSelected);
          filterItemsChanged.push(checkbox);
        }
      }

      // we handle range filters slightly differently here
      const updateRangeFilterItems = (filterGroupType: FilterGroupType, minKey: string, maxKey: string) => {
        const hasFilter = !!activeSearch?.searchQuery.filters.some((f) => f.name === filterGroupType);
        const maxFilter = activeSearch?.searchQuery.filters.find((f) => f.name === filterGroupType)?.values[0]?.range.max;
        const minFilter = activeSearch?.searchQuery.filters.find((f) => f.name === filterGroupType)?.values[0]?.range.min;
        const maxFilterItem = filterItems.find((item) => item.Name === maxKey);
        const minFilterItem = filterItems.find((item) => item.Name === minKey);
        if (hasFilter && minFilterItem.Value !== minFilter) {
          minFilterItem.setIsSelected(true);
          minFilterItem.setValue(parseInt(minFilter, 10) ?? null);
          minFilterItem.Group.setIsActive(true);
          filterItemsChanged.push(minFilterItem);
        }
        if (hasFilter && maxFilterItem.Value !== maxFilter) {
          maxFilterItem.setIsSelected(true);
          maxFilterItem.setValue(parseInt(maxFilter, 10) ?? null);
          maxFilterItem.Group.setIsActive(true);
          filterItemsChanged.push(maxFilterItem);
        }
        if (hasFilter === false) {
          minFilterItem.reset();
          maxFilterItem.reset();
          filterItemsChanged.push(minFilterItem, maxFilterItem);
        }
      };

      updateRangeFilterItems(FilterGroupType.CharacterLength, 'CharacterLengthMin', 'CharacterLengthMax');
      updateRangeFilterItems(FilterGroupType.PriceRange, 'PriceRangeMin', 'PriceRangeMax');

      // now "submit" all the new Filters & sorts
      if (filterItemsChanged.length) this.filterCollection.raiseFiltersChanged(...filterItemsChanged);
      if (newSort) {
        this._tableService.setSorts(
          newSort.field as any,
          newSort.direction === 'Descending' ? SortDirectionType.Descending : SortDirectionType.Ascending
        );
      } else {
        this._tableService.setSorts(null, null);
      }
    });
  }

  private retrieveAggregations(): void {
    this.checkIsNewSearch();

    combineLatest([this.searchTerm$, this.requestedFilters$])
      .pipe(first())
      .subscribe(([searchTerm, filters]) => {
        this._http.send<ApiGetFilters, ITcResponse<IFilterAggregation[]>>(
          new ApiGetFilters(searchTerm, filters)
        ).subscribe(
          response => {
            if (response.result && response.result.length > 0) {
              this.filterCollection.applyAggregations(response.result);
            } else {
              this._loggingService.log(`${this.name}.handleRetrieveFiltersResponse`, Severity.Error, { message: 'Received empty auction aggregation list' });
            }
          }
        );
      });
  }

  private setDefaultFilters(): void {
    const defaultFilterItems = this.filterCollection.Groups.map((g) => g.Items)
      .reduce((a, b) => a.concat(b))
      .filter((x) => x?.IsAlwaysSelected);
    const updatedFilters = this.updateFilterItems([], defaultFilterItems);
    const filterCount = updatedFilters.length === 0 ? 0 : updatedFilters.map((f) => f.Values).reduce((a, b) => a.concat(b)).length - 1;
    this.filterCountSubject.next(filterCount);
    this.searchTermSubject.next('');
    this.requestedFiltersSubject.next(updatedFilters);
    this.retrieveAggregations();
  }

  private updateFilters(...items: FilterItem[]): void {
    this.requestedFilters$.pipe(first()).subscribe((filters) => {
      const updatedFilters = this.updateFilterItems(filters, items);
      const filterCount = updatedFilters.length === 0 ? 0 : updatedFilters.map((f) => f.Values).reduce((a, b) => a.concat(b)).length - 1;
      this.filterCountSubject.next(filterCount);
      this.requestedFiltersSubject.next(updatedFilters);
      this.retrieveAggregations();
    });
  }

  private updateFilterItems(existingFilters: Filter[], changedItems: FilterItem[]): Filter[] {
    let filters = [...existingFilters];
    const items = [...changedItems];

    items.forEach((item) => {
      const existingFilterIndex = filters.findIndex((f) => f.Name === item.Group.GroupType.toString());
      if (item.IsSelected) {
        if (existingFilterIndex > -1) {
          if (item.Group.FilterType === FilterType.Range) {
            let itemValue: number | Date;
            if (typeof item.Value === 'number') {
              itemValue = item.Value != null ? parseInt(item.Value.toString(), 10) : null;
            } else if (item.Value instanceof Date) {
              itemValue = item.Value;
            }
            if (item.ItemType === FilterItemType.Min) {
              filters[existingFilterIndex].Values[0].Range.Min = itemValue ? itemValue : null;
            } else if (item.ItemType === FilterItemType.Max) {
              filters[existingFilterIndex].Values[0].Range.Max = itemValue ? itemValue : null;
            }
          } else {
            const existingFilterItem = filters[existingFilterIndex].Values.find((v) => v.Value === item.ItemType);
            if (existingFilterItem == null) {
              filters[existingFilterIndex].Values.push(new FilterValue(item.ItemType));
            }
          }
        } else {
          if (item.Group.FilterType === FilterType.Range) {
            let filterValueRange: FilterValueRange;
            if (typeof item.Value === 'number') {
              const itemValue = item.Value ? parseInt(item.Value.toString(), 10) : null;
              filterValueRange = item.ItemType === FilterItemType.Min ? new FilterValueRange(itemValue) : new FilterValueRange(null, itemValue);
            } else if (item.Value instanceof Date) {
              filterValueRange = item.ItemType === FilterItemType.Min ? new FilterValueRange(item.Value) : new FilterValueRange(null, item.Value);
            } else {
              filterValueRange = item.ItemType === FilterItemType.Min ? new FilterValueRange(null) : new FilterValueRange(null, null);
            }
            const filterValue = new FilterValue(filterValueRange);
            const filter = new Filter(item.Group.GroupType, filterValue);
            filters.push(filter);
          } else {
            const filterValue = new FilterValue(item.ItemType);
            const filter = new Filter(item.Group.GroupType, filterValue);
            filters.push(filter);
          }
        }
      } else {
        if (existingFilterIndex > -1) {
          if (item.Group.FilterType === FilterType.Range) {
            if (
              filters[existingFilterIndex].Values != null &&
              filters[existingFilterIndex].Values.length > 0 &&
              filters[existingFilterIndex].Values[0].Range != null
            ) {
              if (item.ItemType === FilterItemType.Min) {
                filters[existingFilterIndex].Values[0].Range.Min = null;
              }
              if (item.ItemType === FilterItemType.Max) {
                filters[existingFilterIndex].Values[0].Range.Max = null;
              }
            }
          } else {
            filters[existingFilterIndex].removeValue(item.ItemType);
          }

          if (
            filters[existingFilterIndex].Values.length > 0 &&
            filters[existingFilterIndex].Values[0].Range != null &&
            filters[existingFilterIndex].Values[0].Range.Min == null &&
            filters[existingFilterIndex].Values[0].Range.Max == null
          ) {
            filters[existingFilterIndex].clearValues();
          }
          if (filters[existingFilterIndex].Values.length === 0) {
            filters = [...filters.slice(0, existingFilterIndex), ...filters.slice(existingFilterIndex + 1)];
          }
        }
      }
    });

    return filters;
  }

  private createFilterGroups(): void {
    this.filterCollection.addFilterGroups(
      this.createRecordTypesGroup(),
      this.createTldGroup(),
      this.createEndingTodayGroup(),
      this.createWithBidsGroup(),
      this.createKeywordPlacementGroup(),
      this.createFilterDomainsGroup(),
      this.createCharacterLengthGroup(),
      this.createPriceRangeGroup(),
      this.createExpirationDateGroup()
    );
  }

  private createRecordTypesGroup(): FilterGroup {
    const filterTypes = [FilterItemType.Dropped, FilterItemType.PrivateSeller, FilterItemType.PendingDelete, FilterItemType.PreRelease];
    const recordTypeGroup = new FilterGroup(
      this.filterCollection,
      FilterGroupType.RecordType,
      FilterType.Boolean,
      filterTypes,
      FilterDisplayHintType.CheckBox
    );
    return recordTypeGroup;
  }

  private createFilterDomainsGroup(): FilterGroup {
    const filterTypes = [FilterItemType.NoDashes, FilterItemType.AllNumbers, FilterItemType.NoNumbers];
    const filterDomainsGroup = new FilterGroup(
      this.filterCollection,
      FilterGroupType.FilterDomains,
      FilterType.Boolean,
      filterTypes,
      FilterDisplayHintType.CheckBox
    );
    return filterDomainsGroup;
  }

  private createEndingTodayGroup(): FilterGroup {
    const filterTypes = [FilterItemType.AuctionsEndingToday];
    const endingTodayGroup = new FilterGroup(
      this.filterCollection,
      FilterGroupType.EndingToday,
      FilterType.Boolean,
      filterTypes,
      FilterDisplayHintType.CheckBox,
      false
    );
    return endingTodayGroup;
  }

  private createWithBidsGroup(): FilterGroup {
    const filterTypes = [FilterItemType.AuctionWithBids];
    const withBidsGroup = new FilterGroup(
      this.filterCollection,
      FilterGroupType.WithBids,
      FilterType.Boolean,
      filterTypes,
      FilterDisplayHintType.CheckBox,
      false
    );
    return withBidsGroup;
  }

  private createKeywordPlacementGroup(): FilterGroup {
    const placementGroup = new FilterGroup(this.filterCollection, FilterGroupType.KeywordPlacement, FilterType.Boolean);
    const filterItems = [
      new FilterItem(placementGroup, FilterItemType.KeywordContains, 'KeywordContains', true),
      new FilterItem(placementGroup, FilterItemType.KeywordStartsWith, 'KeywordStartsWith'),
      new FilterItem(placementGroup, FilterItemType.KeywordEndsWith, 'KeywordEndsWith'),
    ];
    placementGroup.setItems(filterItems);
    placementGroup.setDisplayHintType(FilterDisplayHintType.Radio);
    placementGroup.setIsAlwaysVisible(false);
    return placementGroup;
  }

  private createCharacterLengthGroup(): FilterGroup {
    const charGroup = new FilterGroup(this.filterCollection, FilterGroupType.CharacterLength, FilterType.Range);
    const filterItems = [
      new FilterItem(charGroup, FilterItemType.Min, 'CharacterLengthMin', false, false, true),
      new FilterItem(charGroup, FilterItemType.Max, 'CharacterLengthMax', false, false, true),
    ];
    charGroup.setItems(filterItems);
    charGroup.setDisplayHintType(FilterDisplayHintType.Range);
    return charGroup;
  }

  private createPriceRangeGroup(): FilterGroup {
    const priceGroup = new FilterGroup(this.filterCollection, FilterGroupType.PriceRange, FilterType.Range);
    const filterItems = [
      new FilterItem(priceGroup, FilterItemType.Min, 'PriceRangeMin', false, false, true),
      new FilterItem(priceGroup, FilterItemType.Max, 'PriceRangeMax', false, false, true),
    ];
    priceGroup.setItems(filterItems);
    priceGroup.setDisplayHintType(FilterDisplayHintType.Range);
    return priceGroup;
  }

  private createExpirationDateGroup(): FilterGroup {
    const group = new FilterGroup(this.filterCollection, FilterGroupType.ExpirationDate, FilterType.Range);
    const minItem = new FilterItem(group, FilterItemType.Min, 'ExpirationDateMin', false, true, false, true);
    const maxItem = new FilterItem(group, FilterItemType.Max, 'ExpirationDateMax', false, true, false, true);
    minItem.setValue(new Date());
    maxItem.setValue(null);
    group.setItems([minItem, maxItem]);
    group.setDisplayHintType(FilterDisplayHintType.None);
    return group;
  }

  private createTldGroup(): FilterGroup {
    const filterTypes = [
      FilterItemType.COM,
      FilterItemType.ORG,
      FilterItemType.NET,
      FilterItemType.CC,
      FilterItemType.TV,
      FilterItemType.IO,
      FilterItemType.INFO,
    ];
    const tldGroup = new FilterGroup(
      this.filterCollection,
      FilterGroupType.TLD,
      FilterType.Boolean,
      (type) => `.${type.toLowerCase()}`,
      FilterDisplayHintType.CheckBox
    );
    tldGroup.setItems(filterTypes.map((t) => new FilterItem(tldGroup, t, `.${t.toLowerCase()}`, false, false, false, false, true)));
    return tldGroup;
  }
}
